import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ScrollReveal, StaggerContainer, StaggerItem, fadeInLeft, fadeInRight } from "@/components/animations/ScrollAnimations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, Phone, MapPin, Clock, Send, Headphones, Users } from "lucide-react";
import { SEO } from "@/components/SEO";
import { motion } from "framer-motion";
import { z } from "zod";

// Validation schema for contact form
const contactFormSchema = z.object({
  name: z.string()
    .trim()
    .min(1, { message: "Name is required" })
    .max(100, { message: "Name must be less than 100 characters" })
    .regex(/^[a-zA-Z\s'-]+$/, { message: "Name contains invalid characters" }),
  email: z.string()
    .trim()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  phone: z.string()
    .trim()
    .max(20, { message: "Phone must be less than 20 characters" })
    .regex(/^[\d\s\-+()]*$/, { message: "Phone contains invalid characters" })
    .optional()
    .or(z.literal("")),
  company: z.string()
    .trim()
    .max(100, { message: "Company must be less than 100 characters" })
    .optional()
    .or(z.literal("")),
  message: z.string()
    .trim()
    .min(1, { message: "Message is required" })
    .max(2000, { message: "Message must be less than 2000 characters" }),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const contactInfo = [
  {
    icon: MapPin,
    title: "Headquarters",
    details: ["19681 Da Vinci", "Foothill Ranch, CA 92610"],
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    details: ["Toll-Free: 855-699-9191", "Local: 949-945-2000"],
  },
  {
    icon: Users,
    title: "Sales",
    details: ["949-945-6300", "949-534-3000"],
  },
  {
    icon: Mail,
    title: "Email",
    details: ["support@evgateway.com", "sales@evgateway.com"],
  },
];

export default function Contact() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate form data using Zod schema
    const result = contactFormSchema.safeParse(formData);
    
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof ContactFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast({
        title: "Validation Error",
        description: "Please check the form for errors.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate form submission with validated & sanitized data
    // Note: In production, this should send to a secure backend API
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: "Message sent!",
      description: "We'll get back to you within 24 hours.",
    });
    
    setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Contact EVGateway - Request a Demo for EV Charging Solutions"
        description="Contact EVGateway for Fast Charging, V2G, Fleet Charging, and Smart Charging solutions. Request a demo, get pricing, or speak with our 24/7 support team. Headquarters in Foothill Ranch, CA."
        keywords="contact EVGateway, EV charging demo, fast charging quote, fleet charging inquiry, V2G consultation, EV charging support, charging station pricing"
        canonicalUrl="/contact"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Ambient orbs */}
        <motion.div
          className="absolute top-1/4 right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-[5%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--accent) / 0.08) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/30 text-primary text-sm font-medium mb-6"
              >
                <Mail className="h-4 w-4" />
                Get in Touch
              </motion.div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Let's <span className="text-gradient">Power</span> Your
                <br />EV Infrastructure
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Ready to transform your charging network? Our team is here to help 
                you design, deploy, and manage world-class EV charging solutions.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4">
          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, idx) => (
              <StaggerItem key={info.title}>
                <motion.div 
                  whileHover={{ scale: 1.03, y: -5 }}
                  className={`p-6 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 transition-all duration-300 hover:shadow-lg ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${idx % 2 === 0 ? "bg-primary/10" : "bg-accent/10"}`}>
                    <info.icon className={`w-6 h-6 ${idx % 2 === 0 ? "text-primary" : "text-accent"}`} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{info.title}</h3>
                  {info.details.map((detail, detailIdx) => (
                    <p key={detailIdx} className="text-muted-foreground text-sm">{detail}</p>
                  ))}
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Info */}
            <ScrollReveal variants={fadeInLeft}>
              <div>
                <h2 className="text-4xl font-bold mb-6">
                  White-Label Solutions for Your Business
                </h2>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  EVGateway offers comprehensive white-label solutions that put you in control. 
                  Set your own pricing, access remote diagnostics, and get customized reporting 
                  tailored to your business needs.
                </p>
                
                <div className="space-y-6">
                  <motion.div 
                    whileHover={{ x: 5 }}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-background/80 backdrop-blur-sm border border-border/30 hover:border-primary/30 transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Quick Response</h3>
                      <p className="text-muted-foreground text-sm">
                        Our team responds to all inquiries within 24 hours.
                      </p>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    whileHover={{ x: 5 }}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-background/80 backdrop-blur-sm border border-border/30 hover:border-accent/30 transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Headphones className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">24/7 Support</h3>
                      <p className="text-muted-foreground text-sm">
                        Round-the-clock technical support for all your needs.
                      </p>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    whileHover={{ x: 5 }}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-background/80 backdrop-blur-sm border border-border/30 hover:border-primary/30 transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Dedicated Account Manager</h3>
                      <p className="text-muted-foreground text-sm">
                        Get a personal point of contact for your account.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </ScrollReveal>

            {/* Right: Form */}
            <ScrollReveal variants={fadeInRight}>
              <form onSubmit={handleSubmit} className="p-8 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30">
                <h3 className="text-2xl font-bold mb-6">Send us a message</h3>
                
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Full Name <span aria-hidden="true">*</span>
                        <span className="sr-only">(required)</span>
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        aria-required="true"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "name-error" : undefined}
                        placeholder="John Doe"
                        maxLength={100}
                        className={`bg-background/50 border-border/50 focus:border-primary/50 rounded-xl ${errors.name ? 'border-destructive' : ''}`}
                      />
                      {errors.name && (
                        <p id="name-error" className="text-sm text-destructive mt-1">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email <span aria-hidden="true">*</span>
                        <span className="sr-only">(required)</span>
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        aria-required="true"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "email-error" : undefined}
                        placeholder="john@company.com"
                        maxLength={255}
                        className={`bg-background/50 border-border/50 focus:border-primary/50 rounded-xl ${errors.email ? 'border-destructive' : ''}`}
                      />
                      {errors.email && (
                        <p id="email-error" className="text-sm text-destructive mt-1">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2">
                        Phone
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? "phone-error" : undefined}
                        placeholder="+1 (555) 000-0000"
                        maxLength={20}
                        className={`bg-background/50 border-border/50 focus:border-primary/50 rounded-xl ${errors.phone ? 'border-destructive' : ''}`}
                      />
                      {errors.phone && (
                        <p id="phone-error" className="text-sm text-destructive mt-1">{errors.phone}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium mb-2">
                        Company
                      </label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        aria-invalid={!!errors.company}
                        aria-describedby={errors.company ? "company-error" : undefined}
                        placeholder="Your Company"
                        maxLength={100}
                        className={`bg-background/50 border-border/50 focus:border-primary/50 rounded-xl ${errors.company ? 'border-destructive' : ''}`}
                      />
                      {errors.company && (
                        <p id="company-error" className="text-sm text-destructive mt-1">{errors.company}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message <span aria-hidden="true">*</span>
                      <span className="sr-only">(required)</span>
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? "message-error" : undefined}
                      placeholder="Tell us about your project or inquiry..."
                      rows={5}
                      maxLength={2000}
                      className={`bg-background/50 border-border/50 focus:border-primary/50 rounded-xl resize-none ${errors.message ? 'border-destructive' : ''}`}
                    />
                    {errors.message && (
                      <p id="message-error" className="text-sm text-destructive mt-1">{errors.message}</p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    variant="hero" 
                    size="lg" 
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">Visit Our Headquarters</h2>
              <p className="text-muted-foreground">19681 Da Vinci, Foothill Ranch, CA 92610</p>
            </div>
            <div className="rounded-3xl overflow-hidden border border-border/30 bg-background/80 backdrop-blur-sm">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3320.6873991483785!2d-117.6610!3d33.6714!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dce8a23fc0dd4f%3A0x5e0e1e0e0e0e0e0e!2s19681%20Da%20Vinci%2C%20Foothill%20Ranch%2C%20CA%2092610!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="EVGateway Headquarters Location"
                className="w-full"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}